// i18n for the widget. All shopper-facing copy lives here, keyed by language.
// Default is Spanish (es). Add a language by adding one dictionary — nothing else
// in the widget hardcodes copy. Resolution: data-lang on the embed > the page's
// <html lang> prefix > "es".

export type Lang = "es" | "en"

export interface Strings {
  // Inline CTA button (on the merchant page)
  ctaDefault: string
  ctaGenerating: string
  ctaReady: string
  ctaError: string

  // Modal — chrome + upload step
  ariaLabel: string
  closeLabel: string
  dismissLabel: string
  tryAgain: string
  uploadTitle: string
  uploadSubtitle: string
  chooseFrontPhoto: string
  frontPreviewAlt: string
  heightLabel: string
  addSidePhoto: string
  chooseSidePhoto: string
  sidePreviewAlt: string
  consentNote: string
  generateButton: string

  // Modal — loading step
  loadingTitle: string
  loadingSubtitle: string
  keepBrowsing: string

  // Modal — result step
  resultTitle: string
  resultAlt: string
  shareWhatsApp: string
  saveImage: string
  tryAnotherPhoto: string
  deleteMyPhotos: string

  // Modal — deleted step
  deletedTitle: string
  deletedSubtitle: string
  newTryOn: string

  // Inline errors
  errNoProduct: string
  errHeadshot: string
  errInProgress: string
  errDeleteFailed: string

  // Share + back link (interpolated)
  shareText: (productUrl: string) => string
  backToNamed: (name: string) => string
  backToProduct: string

  // Floating toast/pill
  toastGeneratingTitle: (name?: string) => string
  toastGeneratingAction: string
  toastReadyTitle: (name?: string) => string
  toastReadyAction: string
  toastErrorTitle: string
  toastErrorAction: string

  // Size panel
  sizeCalculating: string
  sizeAnalyzing: string
  sizeSuggestedLabel: string
  sizeUnknownLabel: string
  sizeUnavailableLabel: string
  sizeTimeoutNote: string
  sizeRetryNote: string

  // Size recommendation note
  fitTight: string
  fitRegular: string
  fitLoose: string
  confHigh: string
  confMedium: string
  confLimited: string
  estimateBeta: string
  alternativeAlso: (size: string) => string

  // Size reason hints (by reason code) + fallback
  sizeReason: Record<string, string>
  sizeReasonFallback: string

  // Generation errors (by failure class) + fallback
  genError: Record<string, string>
  genErrorFallback: string

  // Photo-check hints
  photoBlockTooSmall: string
  photoWarnSmall: string
  photoWarnDark: string
  photoWarnBright: string
  photoBlockHeadshot: string
  photoGood: string
}

const es: Strings = {
  ctaDefault: "✨ Probá cómo te queda",
  ctaGenerating: "Creando tu prueba…",
  ctaReady: "Ver cómo te queda ✓",
  ctaError: "Reintentar prueba",

  ariaLabel: "Probador virtual",
  closeLabel: "Cerrar",
  dismissLabel: "Descartar",
  tryAgain: "Intentar de nuevo",
  uploadTitle: "Probátelo virtualmente",
  uploadSubtitle: "Subí una foto de frente, de cuerpo entero y con buena luz.",
  chooseFrontPhoto: "Elegir foto de frente",
  frontPreviewAlt: "Vista previa",
  heightLabel: "Altura en centímetros",
  addSidePhoto: "Agregar foto de perfil",
  chooseSidePhoto: "Elegir foto de perfil",
  sidePreviewAlt: "Vista previa lateral",
  consentNote: "Al subir tu foto aceptás que se procese para crear la prueba virtual y estimar tu talle. Las fotos de entrada se conservan 24 horas y el resultado 7 días; luego se eliminan automáticamente.",
  generateButton: "Ver cómo me queda",

  loadingTitle: "Creando tu prueba…",
  loadingSubtitle: "Tarda unos segundos. Podés seguir navegando: te avisamos en este botón cuando esté lista.",
  keepBrowsing: "Seguir navegando",

  resultTitle: "¡Así te queda!",
  resultAlt: "Resultado de la prueba virtual con la prenda puesta",
  shareWhatsApp: "Compartir por WhatsApp",
  saveImage: "Guardar imagen",
  tryAnotherPhoto: "Probar otra foto",
  deleteMyPhotos: "Eliminar mis fotos",

  deletedTitle: "Tus fotos fueron eliminadas",
  deletedSubtitle: "Eliminamos las fotos, el resultado visual y los datos de talle de esta sesión.",
  newTryOn: "Hacer una nueva prueba",

  errNoProduct: "No pudimos identificar este producto.",
  errHeadshot: "Parece una foto de tu cara. Subí una foto de cuerpo entero o de la cintura para arriba.",
  errInProgress: "Ya tenés una prueba en curso. Esperá a que termine antes de probar otra prenda.",
  errDeleteFailed: "No pudimos eliminar tus fotos ahora. Intentá nuevamente.",

  shareText: (url) => `Mirá cómo me queda 👀 ${url}`,
  backToNamed: (name) => `← Volver a ${name}`,
  backToProduct: "← Volver al producto",

  toastGeneratingTitle: (name) => `Creando tu prueba${name ? ` de ${name}` : ""}…`,
  toastGeneratingAction: "Seguí navegando, te avisamos acá",
  toastReadyTitle: (name) => `¡Tu prueba${name ? ` de ${name}` : ""} está lista!`,
  toastReadyAction: "Ver cómo te queda",
  toastErrorTitle: "No pudimos completar la prueba",
  toastErrorAction: "Reintentar",

  sizeCalculating: "Calculando tu talle…",
  sizeAnalyzing: "Analizamos tus medidas mientras preparamos la prueba virtual.",
  sizeSuggestedLabel: "Talle sugerido",
  sizeUnknownLabel: "No pudimos sugerirte un talle con confianza",
  sizeUnavailableLabel: "El cálculo de talle no está disponible",
  sizeTimeoutNote: "El cálculo tardó demasiado. Podés intentar nuevamente con la misma foto.",
  sizeRetryNote: "Tu prueba virtual puede continuar. Intentá calcular el talle nuevamente en unos minutos.",

  fitTight: "calce ajustado",
  fitRegular: "calce regular",
  fitLoose: "calce holgado",
  confHigh: "confianza alta",
  confMedium: "confianza media",
  confLimited: "confianza limitada",
  estimateBeta: "Estimación orientativa — beta",
  alternativeAlso: (size) => `también podría irte ${size}`,

  sizeReason: {
    low_measurement_confidence: "Probá con una foto de cuerpo entero, de frente, con buena luz y sin partes del cuerpo fuera de cuadro.",
    low_confidence: "Probá con una foto de cuerpo entero, de frente, con buena luz y sin partes del cuerpo fuera de cuadro.",
    insufficient_evidence: "No pudimos ver tu cuerpo con claridad. Probá una foto de cuerpo entero, de frente y con buena luz.",
    low_chart_confidence: "Tu medida queda cerca del límite entre talles. Revisá la tabla de la marca o compará con una prenda que ya uses.",
    missing_or_unusable_chart: "Esta prenda todavía no tiene una tabla de talles compatible.",
    combined_output_invalid: "No pudimos calcular tu talle esta vez. Tu prueba virtual está lista igual."
  },
  sizeReasonFallback: "Probá otra foto o consultá la tabla de talles de la marca.",

  genError: {
    timeout: "La prueba tardó demasiado. Intentá nuevamente.",
    rate_limited: "Hay demasiadas pruebas en curso. Esperá un momento.",
    out_of_credits: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    provider_unavailable: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    unsupported_category: "El probador virtual todavía no está disponible para este tipo de prenda. Podés seguir con el resto del proceso normalmente.",
    photo_consent_required: "Necesitamos tu autorización para procesar la foto.",
    invalid_person_image: "La foto no es válida. Elegí un archivo JPEG, PNG o WebP.",
    invalid_front_image: "La foto de frente no es válida. Elegí otra imagen.",
    invalid_side_image: "La foto de perfil no es válida. Elegí otra imagen.",
    product_image_unavailable: "No pudimos preparar la imagen de esta prenda.",
    insufficient_body: "Necesitamos una foto tuya, de frente, de cuerpo entero (o de la cintura para arriba) y que estés solo/a en la imagen.",
    insufficient_feet: "Necesitamos ver tus pies en la foto: una de cuerpo entero o un primer plano de los pies sirve.",
    missing_person_image: "Elegí una foto para probar la prenda.",
    invalid_input: "La foto no es válida. Probá con otra imagen.",
    moderation: "No pudimos procesar esta foto. Probá con otra imagen.",
    network: "Problema de conexión. Revisá tu internet e intentá de nuevo.",
    provider_error: "El probador tuvo un problema. Probá de nuevo en un momento.",
    out_of_memory: "El probador está sobrecargado. Probá de nuevo en un momento.",
    tunnel_down: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    configuration: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    authentication: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    // Internal misroute: a size request landed on a provider that only delivers
    // sizing fused with the try-on call. Invisible in a healthy flow; neutral copy.
    size_requires_tryon: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    // Entitlement/account states — neutral on purpose: a shopper must never see the
    // merchant's billing status, nor think their photo was the problem.
    account_not_active: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    capability_disabled: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    usage_limit_reached: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    payment_overdue: "El probador no está disponible en este momento. Volvé a intentarlo más tarde.",
    generation_failed: "No pudimos generar la prueba con esta imagen."
  },
  genErrorFallback: "No pudimos generar la prueba. Probá de nuevo o con otra foto.",

  photoBlockTooSmall: "La foto es demasiado chica para generar una prueba. Subí una imagen más grande, de frente y de cuerpo entero.",
  photoWarnSmall: "La foto es chica. Para un mejor resultado, usá una imagen más grande y de cuerpo entero.",
  photoWarnDark: "Se ve algo oscura. Probá con luz natural y de frente.",
  photoWarnBright: "Hay mucha luz o contraluz. Probá sin una ventana o lámpara detrás.",
  photoBlockHeadshot: "Parece una foto de tu cara. Para probar la prenda necesitamos una foto de cuerpo entero o de la cintura para arriba.",
  photoGood: "¡Buena foto! Luz y tamaño correctos."
}

const en: Strings = {
  ctaDefault: "Try it on",
  ctaGenerating: "Creating your try-on…",
  ctaReady: "See how it looks ✓",
  ctaError: "Try again",

  ariaLabel: "Virtual try-on",
  closeLabel: "Close",
  dismissLabel: "Dismiss",
  tryAgain: "Try again",
  uploadTitle: "Try it on virtually",
  uploadSubtitle: "Upload a front-facing, full-body photo with good lighting.",
  chooseFrontPhoto: "Choose a front photo",
  frontPreviewAlt: "Preview",
  heightLabel: "Height in centimeters",
  addSidePhoto: "Add a side photo",
  chooseSidePhoto: "Choose a side photo",
  sidePreviewAlt: "Side preview",
  consentNote: "By uploading your photo you agree that it will be processed to create the virtual try-on and estimate your size. Input photos are kept for 24 hours and the result for 7 days; then they are deleted automatically.",
  generateButton: "See how it looks on me",

  loadingTitle: "Creating your try-on…",
  loadingSubtitle: "It takes a few seconds. You can keep browsing — we'll let you know on this button when it's ready.",
  keepBrowsing: "Keep browsing",

  resultTitle: "Here's how it looks!",
  resultAlt: "Virtual try-on result with the item on",
  shareWhatsApp: "Share on WhatsApp",
  saveImage: "Save image",
  tryAnotherPhoto: "Try another photo",
  deleteMyPhotos: "Delete my photos",

  deletedTitle: "Your photos were deleted",
  deletedSubtitle: "We deleted the photos, the visual result and the size data from this session.",
  newTryOn: "Start a new try-on",

  errNoProduct: "We couldn't identify this product.",
  errHeadshot: "This looks like a photo of your face. Upload a full-body or waist-up photo.",
  errInProgress: "You already have a try-on in progress. Wait for it to finish before trying another item.",
  errDeleteFailed: "We couldn't delete your photos right now. Please try again.",

  shareText: (url) => `Check out how it looks on me 👀 ${url}`,
  backToNamed: (name) => `← Back to ${name}`,
  backToProduct: "← Back to product",

  toastGeneratingTitle: (name) => `Creating your try-on${name ? ` of ${name}` : ""}…`,
  toastGeneratingAction: "Keep browsing — we'll tell you here",
  toastReadyTitle: (name) => `Your try-on${name ? ` of ${name}` : ""} is ready!`,
  toastReadyAction: "See how it looks",
  toastErrorTitle: "We couldn't complete the try-on",
  toastErrorAction: "Try again",

  sizeCalculating: "Calculating your size…",
  sizeAnalyzing: "We're analyzing your measurements while we prepare the try-on.",
  sizeSuggestedLabel: "Suggested size",
  sizeUnknownLabel: "We couldn't suggest a size confidently",
  sizeUnavailableLabel: "Size estimation isn't available",
  sizeTimeoutNote: "Size estimation took too long. You can try again with the same photo.",
  sizeRetryNote: "Your try-on can continue. Try estimating the size again in a few minutes.",

  fitTight: "tight fit",
  fitRegular: "regular fit",
  fitLoose: "loose fit",
  confHigh: "high confidence",
  confMedium: "medium confidence",
  confLimited: "limited confidence",
  estimateBeta: "Rough estimate — beta",
  alternativeAlso: (size) => `${size} could also work`,

  sizeReason: {
    low_measurement_confidence: "Try a front-facing, full-body photo with good lighting and no part of your body out of frame.",
    low_confidence: "Try a front-facing, full-body photo with good lighting and no part of your body out of frame.",
    insufficient_evidence: "We couldn't see your body clearly. Try a front-facing, full-body photo with good lighting.",
    low_chart_confidence: "Your measurement is close to the boundary between sizes. Check the brand's size chart or compare with an item you already wear.",
    missing_or_unusable_chart: "This item doesn't have a compatible size chart yet.",
    combined_output_invalid: "We couldn't calculate your size this time. Your try-on is ready anyway."
  },
  sizeReasonFallback: "Try another photo or check the brand's size chart.",

  genError: {
    timeout: "The try-on took too long. Please try again.",
    rate_limited: "There are too many try-ons in progress. Please wait a moment.",
    out_of_credits: "The try-on isn't available right now. Please try again later.",
    provider_unavailable: "The try-on isn't available right now. Please try again later.",
    unsupported_category: "The virtual try-on isn't available for this type of item yet. You can continue with the rest of the process normally.",
    photo_consent_required: "We need your permission to process the photo.",
    invalid_person_image: "The photo isn't valid. Choose a JPEG, PNG or WebP file.",
    invalid_front_image: "The front photo isn't valid. Choose another image.",
    invalid_side_image: "The side photo isn't valid. Choose another image.",
    product_image_unavailable: "We couldn't prepare the image for this item.",
    insufficient_body: "We need a front-facing, full-body photo of you (or from the waist up), with just you in the image.",
    insufficient_feet: "We need to see your feet in the photo: a full-body shot or a close-up of your feet works.",
    missing_person_image: "Choose a photo to try the item on.",
    invalid_input: "The photo isn't valid. Try another image.",
    moderation: "We couldn't process this photo. Please try another image.",
    network: "Connection problem. Check your internet and try again.",
    provider_error: "The try-on hit a problem. Please try again in a moment.",
    out_of_memory: "The try-on is overloaded. Please try again in a moment.",
    tunnel_down: "The try-on isn't available right now. Please try again later.",
    configuration: "The try-on isn't available right now. Please try again later.",
    authentication: "The try-on isn't available right now. Please try again later.",
    // Internal misroute: a size request landed on a provider that only delivers
    // sizing fused with the try-on call. Invisible in a healthy flow; neutral copy.
    size_requires_tryon: "The try-on isn't available right now. Please try again later.",
    // Entitlement/account states — neutral on purpose: a shopper must never see the
    // merchant's billing status, nor think their photo was the problem.
    account_not_active: "The try-on isn't available right now. Please try again later.",
    capability_disabled: "The try-on isn't available right now. Please try again later.",
    usage_limit_reached: "The try-on isn't available right now. Please try again later.",
    payment_overdue: "The try-on isn't available right now. Please try again later.",
    generation_failed: "We couldn't generate the try-on with this image."
  },
  genErrorFallback: "We couldn't generate the try-on. Try again or use another photo.",

  photoBlockTooSmall: "The photo is too small to generate a try-on. Upload a larger, front-facing, full-body image.",
  photoWarnSmall: "The photo is small. For a better result, use a larger, full-body image.",
  photoWarnDark: "It looks a bit dark. Try natural, front-facing light.",
  photoWarnBright: "There's too much light or backlight. Try without a window or lamp behind you.",
  photoBlockHeadshot: "This looks like a photo of your face. To try the item on we need a full-body or waist-up photo.",
  photoGood: "Great photo! Lighting and size are right."
}

export const STRINGS: Record<Lang, Strings> = { es, en }

// data-lang on the embed > the page's <html lang> prefix > "es".
export function resolveLang(dataLang?: string | null, htmlLang?: string | null): Lang {
  const pick = (v?: string | null) => (v || "").trim().slice(0, 2).toLowerCase()
  for (const candidate of [ pick(dataLang), pick(htmlLang) ]) {
    if (candidate === "en" || candidate === "es") return candidate
  }
  return "es"
}
